import { createNanoEvents } from "nanoevents";

// L2 highlight card (plan G1a). A bottom slide-in card, one step bigger than a
// toast: an optional avatar/logo slot, a short title, and a one-line body. The
// whole card links to `url` (the global router click delegate handles it).
export type HighlightCardData = {
	id: number;
	title?: string;
	message: string;
	htmlIsSafe?: boolean;
	imageUrl?: string;
	url?: string;
};

export const emitter = createNanoEvents<{
	highlight: (card: HighlightCardData) => void;
}>();

let id = 0;

export const emitHighlightCard = (card: Omit<HighlightCardData, "id">) => {
	emitter.emit("highlight", { ...card, id });
	id += 1;
};

// Queue state used by MobileHighlightCard: only one card is visible at a time,
// concurrent cards wait in `pending` and are shown in arrival order. Kept as
// pure reducers so the coalesce / advance behavior is unit-testable without
// rendering the component.
export type HighlightQueueState = {
	current: HighlightCardData | undefined;
	pending: HighlightCardData[];
};

export const initialHighlightQueueState: HighlightQueueState = {
	current: undefined,
	pending: [],
};

export const enqueueHighlightCard = (
	state: HighlightQueueState,
	card: HighlightCardData,
): HighlightQueueState => {
	if (state.current === undefined) {
		return { current: card, pending: state.pending };
	}
	return { current: state.current, pending: [...state.pending, card] };
};

// Advance to the next pending card (called when the current card auto-dismisses
// or is tapped). When nothing is pending, `current` becomes undefined and the
// component renders nothing.
export const advanceHighlightQueue = (
	state: HighlightQueueState,
): HighlightQueueState => {
	const [next, ...rest] = state.pending;
	return { current: next, pending: rest };
};
